import { useState } from "react";
import { useIonToast } from "@ionic/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { checkmarkCircle, exit } from "ionicons/icons";
import { Budget } from "model";
import { numberWithCommas } from "pages/BillingPortal/utils";
import { Badge, Colors, fontXSmall, Icon } from "swing-components";

import { api, translate } from "~utils";

type BudgetRowProps = {
  budget: Budget;
  canEdit: boolean;
};

export const tableRowCss = {
  display: "grid",
  gridTemplateColumns: "176px 156px 110px 90px 90px 104px 56px",
  gap: 10,
  borderBottom: `solid 2px ${Colors.slate200}`,
  minHeight: 64,
  padding: "8px 24px",
};

export function BudgetRow(props: BudgetRowProps) {
  const { budget, canEdit } = props;
  const [isEditing, setEditing] = useState(false);
  const [limit, setLimit] = useState(budget.limit || undefined);
  const [status, setStatus] = useState(budget.status);

  const [showToast] = useIonToast();
  const queryClient = useQueryClient();

  const { isPending, mutate } = useMutation({
    mutationFn: async ({ status }: { status: "ACTIVE" | "INACTIVE" }) => {
      return api({
        method: "POST",
        url: `/api/admin/budget/${budget.id}`,
        data: { status, limit },
      });
    },
    onSuccess() {
      showToast({
        position: "top",
        icon: checkmarkCircle,
        message: translate("BUDGETS_EDIT_TOAST", { budgetName: budget.name }),
        duration: 6000,
      });
      queryClient.invalidateQueries({ queryKey: ["budgets"] });
      setEditing(false);
    },
    onError(err) {
      showToast({
        position: "top",
        icon: exit,
        message: JSON.stringify(err.message),
        duration: 4000,
      });
    },
  });

  const onSubmit = () => {
    mutate({ status });
  };

  const onCancel = () => {
    setEditing(false);
    setStatus(budget.status);
    setLimit(budget?.limit || 0);
  };

  return (
    <div
      css={{
        ...tableRowCss,
        "> div": {
          flexDirection: "column",
          display: "flex",
          justifyContent: "center",
        },
        background: isEditing ? "var(--swing-white300)" : "",
        opacity: isPending ? 0.5 : 1,
        cursor: isPending ? "not-allowed" : undefined,
      }}
    >
      <div>{budget.name}</div>
      <div>{budget.subscriber.name}</div>
      <div>{budget.ytdSpent ? `$${numberWithCommas(budget.ytdSpent)}` : "Unknown"}</div>
      <div>
        {isEditing ? (
          <input
            placeholder="0"
            type="number"
            css={{
              border: "solid 1px var(--swing-blue500)",
              background: "white",
              padding: 8,
              borderRadius: 8,
            }}
            value={limit}
            onChange={(e) => {
              setLimit(e.target.valueAsNumber);
            }}
          />
        ) : budget.limit ? (
          `$${numberWithCommas(budget.limit)}`
        ) : (
          <span css={{ color: "var(--swing-slate400)" }}>{translate("NOT_PROVIDED")}</span>
        )}
      </div>
      <UtilizationPct ytdSpent={budget.ytdSpent} limit={budget.limit} />
      {isEditing ? (
        <div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as "ACTIVE" | "INACTIVE")}
            css={{
              background: "white",
              border: "solid 1px var(--swing-slate200)",
              borderRadius: 8,
              padding: "8px 12px",
              fontSize: 14,
            }}
          >
            <option value="ACTIVE">{translate("BUDGETS_ACTIVE")}</option>
            <option value="INACTIVE">{translate("BUDGETS_INACTIVE")}</option>
          </select>
        </div>
      ) : (
        <div css={{ display: "flex", alignItems: "center" }}>
          {budget.status === "INACTIVE" ? (
            <Badge status="light" text="Inactive" />
          ) : (
            <Badge status="success" text="Active" />
          )}
        </div>
      )}
      {canEdit && (
        <div css={{ cursor: "pointer", alignItems: "center" }}>
          {isEditing ? (
            <div>
              <div
                css={{
                  padding: "8px 16px",
                  marginBottom: "8px",
                  background: " var(--swing-blue500)",
                  borderRadius: 99,
                  border: "solid 2px var(--swing-blue500)",
                }}
                onClick={isPending ? undefined : onSubmit}
              >
                <Icon name="Check" color="var(--swing-white200)" />
              </div>
              <div
                onClick={onCancel}
                css={{
                  padding: "8px 16px",
                  borderRadius: 99,
                  border: "solid 2px var(--swing-blue500)",
                }}
              >
                <Icon name="Close" color="var(--swing-blue500)" />
              </div>
            </div>
          ) : (
            <div onClick={() => setEditing(true)}>
              <Icon name="Edit" color="var(--swing-blue500)" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const UtilizationPct = ({ ytdSpent, limit }: { ytdSpent?: number; limit?: number }) => {
  if (ytdSpent === 0) {
    return <div css={{ textAlign: "center" }}>$0</div>;
  }

  if (!ytdSpent || (!ytdSpent && !limit)) {
    return (
      <div
        css={{
          ...fontXSmall(),
          color: Colors.slate400,
          display: "flex",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {translate("BUDGETS_NOT_ENOUGH_DATA")}
      </div>
    );
  }

  if (!limit) {
    return (
      <div
        css={{
          ...fontXSmall(),
          color: Colors.slate400,
          display: "flex",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {translate("BUDGETS_NO_LIMIT")}
      </div>
    );
  }

  return <div css={{ textAlign: "center" }}>{Math.ceil((ytdSpent / limit) * 100)}%</div>;
};
